import heic2any from 'heic2any';
import { UploadFile } from 'components/applets/app-upload';
import * as UTIF from 'utif';
import HttpClient from '../services/HttpClient';

/**
 * Checks if a URI is an S3 URL
 */
export const isS3Url = (uri: string): boolean => {
  try {
    const url = new URL(uri);
    return url.hostname.includes('s3') && url.hostname.includes('amazonaws.com');
  } catch {
    return false;
  }
};

/**
 * Extracts S3 key from S3 URL
 * Example: https://bucket.s3.region.amazonaws.com/uploads/user-xxx/file.ext
 * Returns: uploads/user-xxx/file.ext
 */
const extractS3Key = (uri: string): string | null => {
  try {
    const url = new URL(uri);
    // Remove leading slash from pathname
    const pathname = url.pathname.substring(1);
    return pathname || null;
  } catch {
    return null;
  }
};

/**
 * Fetches a blob from URI using backend proxy if it's an S3 URL
 * This avoids CORS issues by routing through the backend
 * Returns null if proxy fails (including 404)
 */
const fetchBlobViaProxy = async (uri: string): Promise<Blob | null> => {
  if (!isS3Url(uri)) {
    return null;
  }

  const s3Key = extractS3Key(uri);
  if (!s3Key) {
    return null;
  }

  // Check cache first to avoid unnecessary requests
  const cachedStatus = failedFetchCache.get(uri);
  if (cachedStatus === 404) {
    return null;
  }

  try {
    // HttpClient already has baseURL configured, so we just need the relative path
    // The path should be: storage/file/{s3Key}
    const proxyPath = `storage/file/${encodeURIComponent(s3Key)}`;

    const proxyResponse = await HttpClient.get(proxyPath, {
      responseType: 'blob',
    });

    if (proxyResponse.data instanceof Blob) {
      // Success - clear cache if it was there
      failedFetchCache.delete(uri);
      return proxyResponse.data;
    }
    // If axios returns ArrayBuffer, convert to Blob
    if (proxyResponse.data instanceof ArrayBuffer) {
      failedFetchCache.delete(uri);
      return new Blob([proxyResponse.data]);
    }

    return null;
  } catch (proxyError: any) {
    // Get status code to determine how to handle the error
    const status = proxyError.response?.status;

    if (status === 404) {
      // File doesn't exist in S3 via proxy - cache it to avoid retrying
      // This is expected if backend is pointing to different bucket than the URL
      failedFetchCache.set(uri, 404);
      return null;
    }

    // For other errors, don't log to reduce console noise
    // Don't cache non-404 errors as they might be temporary
    return null;
  }
};

// Cache para evitar múltiples intentos del mismo archivo que falló (solo para errores definitivos)
// Usamos un Map para almacenar el status code del error
const failedFetchCache = new Map<string, number>();

/**
 * Fetches a blob from URI, handling CORS errors gracefully
 * If it's an S3 URL, tries backend proxy first, then falls back to direct fetch
 * Returns null if all methods fail
 * 
 * NOTE: This function should only be called when conversion is actually needed.
 * If the image is already displaying correctly, don't call this function.
 */
const fetchBlobSafely = async (uri: string): Promise<Blob | null> => {
  // Check cache - solo evitar retry si fue un 404 definitivo
  const cachedStatus = failedFetchCache.get(uri);
  if (cachedStatus === 404) {
    return null;
  }

  // If it's an S3 URL, try proxy first to avoid CORS
  if (isS3Url(uri)) {
    const proxyBlob = await fetchBlobViaProxy(uri);
    if (proxyBlob) {
      return proxyBlob;
    }

    // Si el proxy falló con 404, aún intentar direct fetch como último recurso
    // porque el archivo podría estar en un bucket diferente (prod vs testing)
    // o en un bucket público con CORS habilitado
    // No cachear el 404 del proxy para permitir intentar fetch directo
    const proxyFailedWith404 = failedFetchCache.get(uri) === 404;
    if (proxyFailedWith404) {
      // Limpiar el cache del proxy para permitir intentar fetch directo
      failedFetchCache.delete(uri);
    }

    // Intentar direct fetch como fallback (incluso si proxy falló con 404)
    // porque el archivo podría estar en un bucket diferente o público
  }

  // Try direct fetch (for non-S3 URLs or as fallback for S3 if proxy failed)
  try {
    const response = await fetch(uri, {
      mode: 'cors',
      credentials: 'omit'
    });
    if (!response.ok) {
      // Solo cachear 404 definitivos
      if (response.status === 404) {
        failedFetchCache.set(uri, 404);
      }
      return null;
    }
    // Si funciona, limpiar el cache
    failedFetchCache.delete(uri);
    return await response.blob();
  } catch (error: any) {
    // Check if it's a CORS error
    const isCorsError =
      error.name === 'TypeError' ||
      error.message?.includes('CORS') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('NetworkError') ||
      error.message?.includes('Network request failed');

    // Para errores de CORS, no cachear porque podrían ser problemas temporales
    // Solo cachear si es claramente un 404
    if (!isCorsError) {
      // No es CORS, podría ser otro tipo de error - no cachear
      return null;
    }

    // Es CORS - no cachear, podría resolverse
    return null;
  }
};

/**
 * Checks if a file is a HEIC/HEIF image
 */
export const isHeicFile = (file: UploadFile | File): boolean => {
  const fileName = file.name?.toLowerCase() || '';
  const mimeType = (file as UploadFile).mime || (file as File).type || '';
  const uri = (file as UploadFile).uri || '';

  return (
    fileName.endsWith('.heic') ||
    fileName.endsWith('.heif') ||
    uri.toLowerCase().includes('.heic') ||
    uri.toLowerCase().includes('.heif') ||
    mimeType === 'image/heic' ||
    mimeType === 'image/heif'
  );
};

/**
 * Checks if a file is a TIFF/TIF image
 */
export const isTiffFile = (file: UploadFile | File): boolean => {
  const fileName = file.name?.toLowerCase() || '';
  const mimeType = (file as UploadFile).mime || (file as File).type || '';
  const uri = (file as UploadFile).uri || '';

  return (
    fileName.endsWith('.tif') ||
    fileName.endsWith('.tiff') ||
    uri.toLowerCase().includes('.tif') ||
    uri.toLowerCase().includes('.tiff') ||
    mimeType === 'image/tiff' ||
    mimeType === 'image/tif'
  );
};

/**
 * Converts TIFF file to JPEG using utif library
 * Returns an UploadFile with the converted JPEG URI
 * If force is true, will attempt conversion even if file doesn't appear to be TIFF
 */
export const convertTiffFileToJpeg = async (
  file: UploadFile,
  force: boolean = false
): Promise<UploadFile | null> => {
  if (!force && !isTiffFile(file)) {
    return null;
  }

  try {
    // If we have the original file, use it directly
    let blob: Blob;
    if (file.originalFile) {
      blob = file.originalFile;
    } else {
      // Otherwise, fetch from URI
      // Handle CORS errors gracefully
      const fetchedBlob = await fetchBlobSafely(file.uri);
      if (!fetchedBlob) {
        // CORS error or fetch failed - cannot convert
        return null;
      }
      blob = fetchedBlob;
    }

    // Convert blob to array buffer
    const arrayBuffer = await blob.arrayBuffer();

    // Decode TIFF using UTIF
    let ifds;
    try {
      ifds = UTIF.decode(arrayBuffer);
      if (!ifds || ifds.length === 0) {
        // Not a valid TIFF file
        return null;
      }
    } catch (decodeError) {
      // Not a TIFF file or corrupted
      return null;
    }

    // Get the first image
    const ifd = ifds[0];
    UTIF.decodeImages(arrayBuffer, ifds);

    // Get image dimensions
    const width = ifd.width;
    const height = ifd.height;

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Could not get canvas context');
      return null;
    }

    // Convert TIFF data to RGBA
    const rgba = UTIF.toRGBA8(ifd);

    // Create ImageData and put it on canvas
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(rgba);
    ctx.putImageData(imageData, 0, 0);

    // Convert canvas to blob as JPEG
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (jpegBlob) => {
          if (!jpegBlob) {
            reject(new Error('Could not convert to blob'));
            return;
          }

          // Create a new File object with JPEG extension
          const jpegFile = new File(
            [jpegBlob],
            file.name.replace(/\.(tif|tiff)$/i, '.jpg'),
            { type: 'image/jpeg' }
          );

          // Create a new UploadFile with the converted JPEG
          resolve({
            ...file,
            name: jpegFile.name,
            type: 'image/*',
            mime: 'image/jpeg',
            uri: URL.createObjectURL(jpegFile),
            originalFile: jpegFile,
          });
        },
        'image/jpeg',
        0.9 // Quality
      );
    });
  } catch (error) {
    console.error('Error converting TIFF file:', error);
    return null;
  }
};

/**
 * Converts HEIC file to JPEG using heic2any library
 * Returns an UploadFile with the converted JPEG URI
 * If force is true, will attempt conversion even if file doesn't appear to be HEIC
 */
export const convertHeicFileToJpeg = async (
  file: UploadFile,
  force: boolean = false
): Promise<UploadFile | null> => {
  if (!force && !isHeicFile(file)) {
    return null;
  }

  try {
    // Get the blob - use originalFile if available, otherwise fetch from URI
    let blob: Blob;
    if (file.originalFile) {
      blob = file.originalFile;
    } else {
      // Fetch from URI when file comes from server
      // Handle CORS errors gracefully
      const fetchedBlob = await fetchBlobSafely(file.uri);
      if (!fetchedBlob) {
        // CORS error or fetch failed - cannot convert
        return null;
      }
      blob = fetchedBlob;
    }

    // Convert HEIC to JPEG using heic2any
    let convertedBlob;
    try {
      convertedBlob = await heic2any({
        blob: blob,
        toType: 'image/jpeg',
        quality: 0.9,
      });
    } catch (heicError) {
      // Not a HEIC file or conversion failed
      return null;
    }

    // heic2any returns an array, get the first result
    const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    if (!jpegBlob) {
      return null;
    }

    // Create a new File object with JPEG extension
    const jpegFile = new File(
      [jpegBlob],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' }
    );

    // Create a new UploadFile with the converted JPEG
    return {
      ...file,
      name: jpegFile.name,
      type: 'image/*',
      mime: 'image/jpeg',
      uri: URL.createObjectURL(jpegFile),
      originalFile: jpegFile,
    };
  } catch (error) {
    console.error('Error converting HEIC file:', error);
    return null;
  }
};
